import React from "react";
import { api } from "../lib/api";

// Crash guard (AI-4). One undefined icon in a render threw and React unmounted the WHOLE admin tree —
// a blank till mid-service, no message, no recovery but a manual reload, the in-progress cart gone.
// This stops a render error from blanking the window: it shows an honest, actionable fallback, and
// where it is mounted inside the shell (around AppLayout's children) the navigation stays standing so
// the panel alone fails. React 19 also exposes createRoot's onUncaughtError / onCaughtError, but those
// are logging hooks only — they cannot render a fallback or offer recovery — so the recovery UI has to
// be a class boundary; we keep the logging here too rather than moving it onto those hooks.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    const componentStack = info && info.componentStack ? info.componentStack : "";
    // Never swallow — log to the console with the component stack, exactly as before the boundary
    // existed, so debugging does not get harder.
    console.error("[tabon] A render error was caught by the app error boundary:", error, componentStack);
    // Fire-and-forget crash log (AI-4), same rule as print_log: never awaited, every error swallowed,
    // so a logging failure can never make a bad situation worse. The server fills in the user. A crash
    // that blanks a live till and leaves no trace is the same problem as a print that silently never
    // happened — this leaves the trace.
    try {
      api.post("/client-error", {
        route: (typeof window !== "undefined" && window.location) ? window.location.pathname : null,
        message: String((error && error.message) || error || "Unknown error"),
        component_stack: componentStack || null,
      }).catch(() => {});
    } catch (_) { /* logging must never make things worse */ }
  }

  render() {
    if (this.state.hasError) {
      // Honest and actionable. No raw stack trace — this can be on a screen a customer sees over the
      // counter; the stack goes to the console and the server, not here.
      return (
        <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "#0A0A0A" }}>
          <div style={{ maxWidth: 420, textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 8 }} aria-hidden="true">⚠️</div>
            <h1 style={{ color: "#fff", fontSize: 20, fontWeight: 800, margin: "0 0 8px" }}>Something went wrong</h1>
            <p style={{ color: "#A0A0A0", fontSize: 14, lineHeight: 1.5, margin: "0 0 20px" }}>
              This screen hit an unexpected error. Anything unsaved on it may be lost. Reload to carry on —
              the rest of your data is safe.
            </p>
            <button
              type="button"
              onClick={() => { try { window.location.reload(); } catch (_) {} }}
              style={{ background: "#9D00FF", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
